import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Field, PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Pencil, Trash2, Search } from "lucide-react";
import { etiquetaConductor } from "@/components/ConductorSelect";
import { esEmail, esIdConductor } from "@/lib/validacionEs";
import { LEN } from "@/lib/limites";
import { useAuth } from "@/auth/AuthContext";

const PAGE_SIZE = 50;
const A = String.fromCharCode(0xe1);
const O = String.fromCharCode(0xf3);

function claveTra(t) {
  const nif = String(t?.nif || t?.transportista_nif || "").trim().toUpperCase();
  if (nif) return nif;
  if (t?.codigo != null && t.codigo !== "") return "COD-" + String(t.codigo);
  return "";
}

function etiquetaTra(t) {
  return (t?.razon_social || t?.transportista_razon_social || "").trim()
    || (t?.codigo != null && t.codigo !== "" ? "C" + String.fromCharCode(0xf3) + "d. " + t.codigo : claveTra(t));
}

function nifReal(t) {
  const n = String(t?.nif || "").trim();
  if (!n || n.toUpperCase().startsWith("COD-")) return "";
  return n;
}

function mismoTra(a, b) {
  if (!a || !b) return false;
  const na = nifReal(a);
  const nb = nifReal(b);
  if (na && nb) return na === nb;
  const ca = String(a.codigo ?? "");
  const cb = String(b.codigo ?? "");
  return ca !== "" && ca === cb;
}

export default function ConductoresMantPage() {
  const { user } = useAuth();
  const usaSage = user?.sage_conectado === true;
  const [transportistas, setTransportistas] = useState([]);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ nombre: "", dni: "", telefono: "", email: "" });
  const [agenciaEmail, setAgenciaEmail] = useState("");
  const [agenciaBusy, setAgenciaBusy] = useState(false);
  const [loadingTra, setLoadingTra] = useState(false);

  const loadTransportistas = async () => {
    setLoadingTra(true);
    try {
      const { data: partes } = await api.get("/api/v1/partes", {
        params: { tipo: "transportista", page: 1, page_size: PAGE_SIZE, buscar: q || undefined },
      });
      const list = [];
      const seen = new Set();
      const add = (t) => {
        const key = claveTra(t) || etiquetaTra(t).toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        list.push({
          nif: nifReal(t) || claveTra(t),
          codigo: t.codigo,
          razon_social: etiquetaTra(t),
          email: t.email || "",
        });
      };
      (partes.items || []).forEach(add);
      if (!usaSage) {
        const [{ data }, { data: cats }] = await Promise.all([
          api.get("/api/v1/decas/manual/opciones"),
          api.get("/api/v1/conductores"),
        ]);
        (data.transportistas || []).forEach(add);
        for (const c of cats.items || []) {
          add({ nif: c.transportista_nif, razon_social: c.transportista_razon_social, email: "", codigo: c.transportista_codigo });
        }
      }
      list.sort((a, b) => (a.razon_social || "").localeCompare(b.razon_social || "", "es"));
      setTransportistas(list);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoadingTra(false);
    }
  };

  const loadConductores = async (t) => {
    if (!t) { setItems([]); return; }
    const nif = nifReal(t);
    const codigo = t.codigo != null && t.codigo !== "" ? t.codigo : undefined;
    if (!nif && !codigo) { setItems([]); return; }
    try {
      const { data } = await api.get("/api/v1/conductores", {
        params: {
          transportista_nif: nif || undefined,
          transportista_codigo: codigo,
          page: 1,
          page_size: 200,
        },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);
  useEffect(() => { loadTransportistas(); }, [q, usaSage]); // eslint-disable-line
  useEffect(() => {
    loadConductores(sel);
    setAgenciaEmail(sel?.email || "");
  }, [sel]); // eslint-disable-line

  const filtrados = transportistas;

  const openNew = () => {
    setEdit(null);
    setForm({ nombre: "", dni: "", telefono: "", email: "" });
    setOpen(true);
  };
  const openEdit = (c) => {
    setEdit(c);
    setForm({ nombre: c.nombre || "", dni: c.dni || "", telefono: c.telefono || "", email: c.email || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!sel || (!nifReal(sel) && (sel.codigo == null || sel.codigo === ""))) return;
    if (!form.nombre.trim()) { toast.error("Indica el nombre"); return; }
    if (!form.dni.trim()) { toast.error("El DNI / NIE del conductor es obligatorio"); return; }
    if (!esIdConductor(form.dni)) {
      toast.error("Documento del conductor no v" + String.fromCharCode(0xe1) + "lido");
      return;
    }
    if (form.email.trim() && !esEmail(form.email)) {
      toast.error("Correo no v" + String.fromCharCode(0xe1) + "lido");
      return;
    }
    try {
      const payload = {
        transportista_nif: nifReal(sel) || claveTra(sel),
        transportista_razon_social: sel.razon_social,
        nombre: form.nombre.trim(),
        dni: form.dni.trim() || null,
        telefono: form.telefono.trim() || null,
        email: form.email.trim() || null,
        activo: true,
      };
      if (edit) await api.patch(`/api/v1/conductores/${edit.id}`, payload);
      else await api.post("/api/v1/conductores", payload);
      setOpen(false);
      await loadConductores(sel);
      toast.success("Conductor guardado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const saveAgenciaEmail = async () => {
    if (!nifReal(sel)) return;
    const mail = agenciaEmail.trim();
    if (mail && !esEmail(mail)) {
      toast.error("Correo no v" + String.fromCharCode(0xe1) + "lido");
      return;
    }
    setAgenciaBusy(true);
    try {
      const { data } = await api.patch("/api/v1/partes", {
        tipo: "transportista",
        nif: sel.nif,
        razon_social: sel.razon_social,
        email: mail || null,
      });
      const next = { ...sel, email: data.email || "" };
      setSel(next);
      setTransportistas((rows) => rows.map((t) => t.nif === sel.nif ? { ...t, email: data.email || "" } : t));
      toast.success("Correo de la agencia guardado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setAgenciaBusy(false);
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/api/v1/conductores/${c.id}`);
      await loadConductores(sel);
      toast.success("Conductor eliminado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (!user?.sage_conductores) return <Navigate to="/" replace />;

  return (
    <div>
      <PageHeader
        title="Conductores por transportista"
        subtitle={"Busca el transportista como en Sage y ver" + A + "s sus conductores. El DeCA no se manda solo: se prellenan al enviar."}
        testid="page-conductores-mant"
      />
      <div className="grid lg:grid-cols-[320px_1fr] gap-4">
        <Card className="p-3">
          <div className="relative mb-2">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400"/>
            <Input className="pl-8" value={qInput} onChange={(e)=>setQInput(e.target.value)} placeholder={"Buscar transportista, NIF o c" + O + "digo"} aria-label="Buscar transportista o NIF" data-testid="conductores-buscar-tra"/>
          </div>
          <div className="max-h-[70vh] overflow-auto space-y-1">
            {filtrados.map((t) => (
              <button
                type="button"
                key={t.nif || t.codigo || t.razon_social}
                onClick={() => setSel(t)}
                className={`w-full text-left rounded-md px-3 py-2 border ${mismoTra(sel, t) ? "border-blue-700 bg-blue-50" : "border-transparent hover:bg-slate-50"}`}
              >
                <div className="text-sm font-semibold text-slate-900 truncate">{t.razon_social}</div>
                <div className="text-[11px] font-mono text-slate-500">
                  {nifReal(t) ? nifReal(t) : (t.codigo != null && t.codigo !== "" ? "C" + O + "d. " + t.codigo : "sin NIF")}
                </div>
              </button>
            ))}
            {loadingTra ? <div className="text-sm text-slate-500 p-3">Cargando{String.fromCharCode(0x2026)}</div> : null}
            {!loadingTra && filtrados.length === 0 ? <div className="text-sm text-slate-500 p-3">{q ? "Sin coincidencias en Sage." : ("No hay transportistas. Cr" + String.fromCharCode(0xe9) + "alos en Transportistas o vendr" + String.fromCharCode(0xe1) + "n de Sage.")}</div> : null}
          </div>
        </Card>

        <Card className="p-4">
          {!sel ? (
            <div className="py-16 text-center text-slate-500">
              <Users className="h-8 w-8 mx-auto text-slate-400 mb-2"/>
              Selecciona un transportista a la izquierda.
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <div className="label-tag">Transportista</div>
                  <div className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Chivo" }}>{sel.razon_social}</div>
                  <div className="text-xs font-mono text-slate-500">
                    {sel.nif && !String(sel.nif).startsWith("COD-") ? ("NIF " + sel.nif) : (sel.codigo != null && sel.codigo !== "" ? "C" + String.fromCharCode(0xf3) + "d. " + sel.codigo : "")}
                  </div>
                </div>
                <Button className="bg-blue-700 hover:bg-blue-800" onClick={openNew}><Plus className="h-4 w-4 mr-2"/> Nuevo conductor</Button>
              </div>
              <div className="mb-4 p-3 rounded-md border border-slate-200 bg-slate-50">
                <div className="flex items-end gap-2">
                  <Field className="flex-1" label={"Correo de env" + String.fromCharCode(0xed) + "o de la agencia"}>
                    <Input
                      type="email"
                      value={agenciaEmail}
                      onChange={(e)=>setAgenciaEmail(e.target.value)}
                      placeholder="trafico@agencia.com"
                      data-testid="agencia-email"
                    />
                  </Field>
                  <Button variant="outline" disabled={agenciaBusy} onClick={saveAgenciaEmail}>Guardar</Button>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Si el conductor no tiene correo propio, este es el que se propone al enviar el DeCA.
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="deca-table w-full">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>DNI</th>
                      <th>{"Tel" + String.fromCharCode(0xe9) + "fono"}</th>
                      <th>Correo</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c) => {
                      const sage = c.sage === true || c.fuente === "sage" || String(c.id || "").startsWith("sage-");
                      return (
                      <tr key={c.id}>
                        <td className="font-medium">{c.nombre}</td>
                        <td className="font-mono text-xs">{c.dni || "\u2014"}</td>
                        <td className="text-sm">{c.telefono || "\u2014"}</td>
                        <td className="text-sm">{c.email || "\u2014"}</td>
                        <td className="text-right whitespace-nowrap">
                          {sage ? <span className="text-[11px] text-slate-400">Sage</span> : (
                            <>
                          <Button size="sm" variant="outline" className="mr-2" onClick={()=>openEdit(c)} aria-label="Editar conductor"><Pencil className="h-3.5 w-3.5" aria-hidden="true"/></Button>
                          <Button size="sm" variant="outline" className="text-red-700" onClick={()=>remove(c)} aria-label="Eliminar conductor"><Trash2 className="h-3.5 w-3.5" aria-hidden="true"/></Button>
                            </>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                    {items.length === 0 ? (
                      <tr><td colSpan={5} className="py-10 text-center text-slate-500">{"Todav" + String.fromCharCode(0xed) + "a no hay conductores para " + sel.razon_social + "."}</td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{edit ? "Editar conductor" : "Nuevo conductor"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">{sel?.razon_social} {sel?.nif ? `(${sel.nif})` : ""}</div>
            <Field label="Nombre y apellidos *">
              <Input className="mt-1" maxLength={LEN.nombre} value={form.nombre} onChange={(e)=>setForm({...form, nombre:e.target.value})}/>
            </Field>
            <Field label="DNI / NIF">
              <Input className="mt-1" maxLength={LEN.dni} value={form.dni} onChange={(e)=>setForm({...form, dni:e.target.value})}/>
            </Field>
            <Field label={"Tel" + String.fromCharCode(0xe9) + "fono"}>
              <Input className="mt-1" inputMode="tel" maxLength={LEN.telefono} value={form.telefono} onChange={(e)=>setForm({...form, telefono:e.target.value})}/>
            </Field>
            <Field label="Correo (opcional)">
              <Input className="mt-1" type="email" maxLength={LEN.email} value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} placeholder="si no hay, se usa el de la agencia"/>
            </Field>
            {form.nombre ? <div className="text-xs text-slate-500">{"En el DeCA saldr" + String.fromCharCode(0xe1) + ": "}<b>{etiquetaConductor(form)}</b></div> : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={save}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
